/**
 * Aggiorna una ricerca: la stessa domanda, settimane dopo (F-140).
 *
 * ## Cosa si può cambiare, e cosa no
 *
 * Il **tema** no: un altro tema è un'altra ricerca, e il confronto fra le due
 * non avrebbe senso. La motivazione e i parametri sì — quelli che non si
 * toccano restano dell'originale, così l'aggiornamento è «la stessa domanda,
 * tre settimane dopo» e non una domanda diversa.
 *
 * ## ⚠️ La rotazione automatica si decide QUI, dalla persona
 *
 * La voce di TODO chiede che l'aggiornamento produca «direttamente una
 * rotazione consapevole della precedente, con inserimento in watchlist e
 * wishlist». La wishlist alimenta i candidati dell'auto-trading: un inserimento
 * che partisse da solo sarebbe un'azione a un passo dal denaro decisa da
 * nessuno. Quindi la scelta — rotazione sì/no, e con quali limiti di wishlist —
 * si fa adesso, si vede, e viene persistita: fra un mese spiega perché quella
 * rotazione è partita da sola con quei limiti.
 */

import { useState, type FormEvent } from 'react';
import { Alert, Button, Field, Modal, Select, Switch } from '@/ui';
import { usePortfolios } from '@/features/portfolios/api';
import type { DiscoveryRun, RefreshRunInput } from './api';

type Props = {
  run: DiscoveryRun;
  pending: boolean;
  error: string | null;
  onConfirm: (input: RefreshRunInput) => void;
  onClose: () => void;
};

export function RefreshRunModal({ run, pending, error, onConfirm, onClose }: Props) {
  const portfolios = usePortfolios();
  const [themeDescription, setThemeDescription] = useState(run.themeDescription ?? '');
  const [maxResults, setMaxResults] = useState(String(run.filters.maxResults));
  const [trendingOnly, setTrendingOnly] = useState(run.filters.trendingOnly);
  const [autoRotate, setAutoRotate] = useState(false);
  const [addToWishlist, setAddToWishlist] = useState(false);
  const [portfolioId, setPortfolioId] = useState('');
  const [maxExposureAmount, setMaxExposureAmount] = useState('');
  const [maxPortfolioPercentage, setMaxPortfolioPercentage] = useState('');

  const options = (portfolios.data?.items ?? []).map((portfolio) => ({
    value: portfolio.id,
    label: `${portfolio.name} (${portfolio.baseCurrency})`,
  }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsedMax = Number(maxResults);
    onConfirm({
      ...(themeDescription.trim() !== (run.themeDescription ?? '') ? { themeDescription: themeDescription.trim() } : {}),
      filters: {
        ...(Number.isInteger(parsedMax) && parsedMax > 0 ? { maxResults: parsedMax } : {}),
        trendingOnly,
      },
      autoRotate,
      ...(autoRotate && addToWishlist && portfolioId
        ? {
            wishlistOptions: {
              portfolioId,
              ...(maxExposureAmount.trim() ? { maxExposureAmount: maxExposureAmount.trim() } : {}),
              ...(maxPortfolioPercentage.trim() ? { maxPortfolioPercentage: maxPortfolioPercentage.trim() } : {}),
            },
          }
        : {}),
    });
  };

  return (
    <Modal title="Aggiorna questa ricerca" onClose={onClose}>
      <form onSubmit={submit} className="rotation-confirm">
        <p>
          Rifaccio la ricerca su <strong>«{run.themeTitle}»</strong> come una ricerca nuova, e al termine
          la <strong>confronto con questa</strong>: quali titoli sono rimasti, quali sono nuovi, quali sono
          spariti, e come si è mosso il mercato nel frattempo. Costa una ricerca della quota di oggi.
        </p>

        <Field
          label="Motivazione (facoltativa)"
          value={themeDescription}
          onChange={(event) => setThemeDescription(event.target.value)}
          hint="Il tema non si cambia: un altro tema sarebbe un'altra ricerca. La motivazione sì."
        />
        <Field
          label="Quanti titoli"
          value={maxResults}
          onChange={(event) => setMaxResults(event.target.value)}
          numeric
          hint={`L'originale ne chiedeva ${run.filters.maxResults}. È anche un tetto di costo.`}
        />
        <Switch label="Solo titoli con un catalizzatore recente" checked={trendingOnly} onChange={setTrendingOnly} />

        <hr className="rotation-confirm__divider" />

        {/* ⚠️ Spento per difetto: chi non tocca niente ottiene il comportamento di
            sempre, cioe' nessun automatismo. */}
        <Switch
          label="Al termine, avvia da sola la rotazione generale"
          checked={autoRotate}
          onChange={(next) => {
            setAutoRotate(next);
            if (!next) setAddToWishlist(false);
          }}
        />
        {autoRotate && (
          <>
            <p className="cell--minor">
              La rotazione saprà cosa fu eletto la volta scorsa, e dirà se quella tesi ha tenuto. Gli eletti
              entrano in watchlist.
            </p>
            <Switch label="…e anche in wishlist, con questi limiti" checked={addToWishlist} onChange={setAddToWishlist} />
            {addToWishlist && (
              <>
                <Alert variant="warning" title="La wishlist alimenta l'auto-trading">
                  Un titolo in wishlist diventa un candidato all'acquisto al prossimo digest. Questa scelta viene
                  registrata insieme all'aggiornamento: fra un mese spiegherà perché quella riga ha questi
                  limiti.
                </Alert>
                <Select
                  label="Portafoglio"
                  value={portfolioId}
                  onChange={(event) => setPortfolioId(event.target.value)}
                  options={options}
                  placeholder="Scegli un portafoglio…"
                  required
                />
                <Field
                  label="Esposizione massima (facoltativa)"
                  value={maxExposureAmount}
                  onChange={(event) => setMaxExposureAmount(event.target.value)}
                  numeric
                />
                <Field
                  label="Peso massimo % (facoltativo)"
                  value={maxPortfolioPercentage}
                  onChange={(event) => setMaxPortfolioPercentage(event.target.value)}
                  numeric
                />
              </>
            )}
          </>
        )}

        {error && (
          <Alert variant="error" title="Aggiornamento non avviato">
            {error}
          </Alert>
        )}

        <div className="rotation-confirm__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            Annulla
          </Button>
          <Button type="submit" loading={pending} disabled={autoRotate && addToWishlist && !portfolioId}>
            Aggiorna la ricerca
          </Button>
        </div>
      </form>
    </Modal>
  );
}
